import React from 'react';
import { motion } from 'framer-motion';
/**
 * PowersOfTenStrip — Horizontal strip showing powers of ten (10⁰ … 10ⁿ).
 *
 * Didactic anchor for the GET-WETENSCHAP families: visualizes the
 * "ladder" of powers of ten so students can ground 4 × 10⁵ in its
 * place on the strip and read off "tienduizenden / honderdduizenden".
 *
 * Cells show both LaTeX-style superscript (10^n) and the expanded
 * decimal value (1, 10, 100, …). Highlighted cells use the module
 * accent color and a soft glow to anchor the answer position.
 *
 * Data shape (matches VISUAL_AID_SCHEMAS.json:powersOfTenStrip):
 *   - minExponent?: number (default 0)
 *   - maxExponent?: number (default 8)
 *   - highlightExponents?: number[] (cells to emphasise)
 *   - showValues?: boolean (default true) — show 1, 10, 100, …
 *   - label?: string
 *   - mode?: 'stimulus' | 'support'
 */
export interface PowersOfTenStripProps {
  minExponent?: number;
  maxExponent?: number;
  highlightExponents?: number[];
  showValues?: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
const NL_NUMBER = new Intl.NumberFormat('nl-NL');
function formatPower(exp: number): string {
  if (exp < 0) return '';
  // 10^0 = 1, 10^1 = 10, …
  if (exp === 0) return '1';
  if (exp <= 8) {
    // 10^n as 1 followed by n zeros, formatted with NL thousands separators
    return NL_NUMBER.format(Math.pow(10, exp));
  }
  return `10^${exp}`;
}
export function PowersOfTenStrip({
  minExponent = 0,
  maxExponent = 8,
  highlightExponents,
  showValues = true,
  label,
  mode = 'support'
}: PowersOfTenStripProps) {
  const lo = Math.max(0, Math.min(minExponent, maxExponent));
  const hi = Math.max(lo, Math.min(20, Math.max(minExponent, maxExponent)));
  const highlights = new Set(highlightExponents ?? []);
  const exponents: number[] = [];
  for (let e = lo; e <= hi; e++) exponents.push(e);
  const isStimulus = mode === 'stimulus';
  return <figure className={isStimulus ? 'flex flex-col items-center gap-2 py-2' : 'flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm'} data-id="element-12438">
      {label && <figcaption className="text-sm font-medium text-slate-600" data-id="element-12439">
          {label}
        </figcaption>}

      <div className="flex w-full max-w-[min(720px,95vw)] items-stretch gap-1 overflow-x-auto" role="list" aria-label="Machten van tien" data-id="element-12440">
        {exponents.map((exp, i) => {
        const isHighlighted = highlights.has(exp);
        return <motion.div key={exp} role="listitem" initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.03,
          duration: 0.25
        }} className={['flex min-w-[56px] flex-1 flex-col items-center justify-between rounded-lg border-2 px-2 py-2 text-center transition-colors', isHighlighted ? 'border-indigo-500 bg-indigo-50 shadow-[0_0_0_3px_rgba(99,102,241,0.15)]' : 'border-slate-200 bg-slate-50'].join(' ')} data-id="element-12441">
              <div className={['font-bold leading-tight', isHighlighted ? 'text-indigo-700' : 'text-slate-700'].join(' ')} style={{
            fontSize: 'clamp(14px, 2.6vw, 18px)'
          }} data-id="element-12442">
                10
                <sup className="text-[0.7em] font-bold" data-id="element-12443">{exp}</sup>
              </div>
              {showValues && <div className={['mt-1 font-mono leading-tight', isHighlighted ? 'text-indigo-600' : 'text-slate-500'].join(' ')} style={{
            fontSize: 'clamp(10px, 1.8vw, 12px)'
          }} data-id="element-12444">
                  {formatPower(exp)}
                </div>}
            </motion.div>;
      })}
      </div>

      {!isStimulus && <p className="text-xs text-slate-500" data-id="element-12445">
          Elke stap naar rechts is <span className="font-bold" data-id="element-12446">×10</span> — één
          nul erbij, één plaats hoger.
        </p>}
    </figure>;
}